import React, { useState, useEffect, useCallback, useRef } from 'react';

// Импортируем вынесенные компоненты
import MainMenu from './components/MainMenu';
import DepositScreen from './components/DepositScreen';
import WechatScreen from './components/WechatScreen';
import ProfileScreen from './components/ProfileScreen';
import SupportScreen from './components/SupportScreen';
import OrderConfirmationScreen from './components/OrderConfirmationScreen';
import { track, setTrackerWS } from './utils/tracker';

// Logger - только для development (в production логи отключены)
const isDev = process.env.NODE_ENV === 'development';
// eslint-disable-next-line no-console
const logger = {
  log: (...args) => isDev && console.log(...args),
  error: (...args) => console.error(...args),
  warn: (...args) => isDev && console.warn(...args),
};

// ========== ERROR BOUNDARY ==========
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App crash:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', padding: 20, textAlign: 'center',
          fontFamily: 'sans-serif', background: '#030712', color: '#fff',
        }}>
          <div>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 20px',
              background: 'rgba(239,68,68,0.15)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 28,
            }}>!</div>
            <h2 style={{ marginBottom: 8, fontSize: 18 }}>Что-то пошло не так</h2>
            <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>
              Попробуйте перезапустить приложение
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 24px', borderRadius: 12, border: 'none',
                background: '#7c3aed', color: '#fff', fontSize: 14,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Перезапустить
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ExchangeApp() {
  const [currentScreen, setCurrentScreen] = useState('main');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [amount, setAmount] = useState('');
  const [fullName, setFullName] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [wechatPaymentMethod, setWechatPaymentMethod] = useState('');
  const [wechatAmount, setWechatAmount] = useState('');
  const [wechatFullName, setWechatFullName] = useState('');
  const [wechatQrCode, setWechatQrCode] = useState(null);
  const [tg, setTg] = useState(null);
  const [userId, setUserId] = useState(null);
  const [telegramUsername, setTelegramUsername] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userPhotoUrl, setUserPhotoUrl] = useState(null);
  const [profileTab, setProfileTab] = useState('requests');
  const [profileActiveSection, setProfileActiveSection] = useState(null); // null = menu, or 'limits', 'requests', 'kyc', 'referral', 'stats'
  const [selectedBank, setSelectedBank] = useState('');
  const [wechatSelectedBank, setWechatSelectedBank] = useState('');
  const [usdtNetwork, setUsdtNetwork] = useState('');
  const [wechatUsdtNetwork, setWechatUsdtNetwork] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [usdtToCnyRate, setUsdtToCnyRate] = useState(null);
  const [limits, setLimits] = useState({ minAmount: 0, maxAmount: 20000000, currency: 'CNY' });
  
  // Рассчитанные суммы для оплаты
  const [calculatedAmounts, setCalculatedAmounts] = useState({
    alipay: { rub: null, usdt: null, loading: false },
    wechat: { rub: null, usdt: null, loading: false },
  });
  
  // Состояние отправки заявки (защита от двойного клика)
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Лимиты на способы оплаты (СБП, ВТБ)
  const [paymentLimits, setPaymentLimits] = useState({
    sbp: {
      name: 'СБП',
      perTransaction: 100000,
      daily: { limit: 100000, used: 0, remaining: 100000, percent: 0 },
      monthly: { limit: 500000, used: 0, remaining: 500000, percent: 0 },
    },
    vtb: {
      name: 'ВТБ',
      perTransaction: 300000,
      daily: { limit: 1000000, used: 0, remaining: 1000000, percent: 0 },
      monthly: { limit: 1000000, used: 0, remaining: 1000000, percent: 0 },
    },
    currency: 'RUB',
  });

  // ========== APP READY STATE ==========
  const [isAppReady, setIsAppReady] = useState(false);
  const [initError, setInitError] = useState(null);

  // ========== ALTYN VERIFICATION STATE ==========
  const [userPhone, setUserPhone] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState({
    isLoading: true,
    isVerified: false,
    isAdmin: false,
    identificationLevel: 0,
    passportState: 'not_registered',
    phone: null,
  });
  const [sbpBanks, setSbpBanks] = useState([]);

  // API URL
  const API_URL = 'https://api.kit8exchange.com';

  // Данные пользователя
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    totalAmountCNY: 0,
    totalAmountRUB: 0,
  });

  // Функция для тактильной обратной связи (вибрация)
  const triggerHaptic = useCallback((type = 'light') => {
    try {
      // Telegram WebApp HapticFeedback
      if (tg?.HapticFeedback) {
        if (type === 'selection') {
          tg.HapticFeedback.selectionChanged();
        } else if (type === 'success') {
          tg.HapticFeedback.notificationOccurred('success');
        } else if (type === 'error') {
          tg.HapticFeedback.notificationOccurred('error');
        } else if (type === 'warning') {
          tg.HapticFeedback.notificationOccurred('warning');
        } else {
          // light, medium, heavy, rigid, soft
          tg.HapticFeedback.impactOccurred(type);
        }
        return;
      }
      
      // Fallback: Navigator Vibration API
      if (navigator.vibrate) {
        const durations = {
          light: 10,
          medium: 20,
          heavy: 30,
          selection: 5,
          success: [10, 50, 10],
          error: [50, 30, 50],
          warning: [30, 20, 30],
        };
        navigator.vibrate(durations[type] || 10);
      }
    } catch (e) {
      // Игнорируем ошибки вибрации
    }
  }, [tg]);

  // Навигация с вибрацией
  const navigateTo = useCallback((screen) => {
    triggerHaptic('light');
    setCurrentScreen(screen);
    const screenMap = { main: 'page_home', deposit: 'page_deposit', wechat: 'page_deposit', profile: 'page_profile', support: 'page_support', orderConfirmation: 'page_orders' };
    if (screenMap[screen]) track(screenMap[screen]);
  }, [triggerHaptic]);

  // Функция для выполнения авторизованных запросов
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    const sessionToken = localStorage.getItem('sessionToken');
    const storedUserId = localStorage.getItem('userId');
    
    if (!sessionToken || !storedUserId) {
      throw new Error('Сессия не найдена. Пожалуйста, перезапустите приложение.');
    }
    
    // Валидация токена (базовая проверка формата)
    if (typeof sessionToken !== 'string' || sessionToken.length < 10) {
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('userId');
      throw new Error('Недействительный токен. Перезапустите приложение.');
    }
    
    const defaultHeaders = {
      'Authorization': `Bearer ${sessionToken}`,
      'X-User-ID': storedUserId,
    };
    
    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }
    
    return fetch(url, {
      ...options,
      credentials: 'omit', // Не отправлять cookies на API
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    });
  }, []);

  // ========== CHECK ADMIN STATUS ==========
  const checkAdminStatus = useCallback(async () => {
    try {
      
      const response = await makeAuthenticatedRequest(`${API_URL}/api/user/is-admin`);
      
      if (response.ok) {
        const data = await response.json();
        logger.log('✅ Статус админа:', data);
        
        if (data.success && data.isAdmin) {
          setIsAdmin(true);
          
          // Админ автоматически верифицирован
          setVerificationStatus({
            isLoading: false,
            isVerified: true,
            isAdmin: true,
            identificationLevel: 999,
            passportState: 'admin',
            phone: null,
            skipVerification: data.skipVerification,
          });
          
          return { isAdmin: true, skipVerification: data.skipVerification };
        }
      }
      
      return { isAdmin: false };
    } catch (error) {
      logger.log('ℹ️ Пользователь не админ или ошибка:', error.message);
      return { isAdmin: false };
    }
  }, [API_URL, makeAuthenticatedRequest]);

  // ========== ALTYN: CHECK VERIFICATION STATUS ==========
  const checkVerificationStatus = useCallback(async (phone) => {
    // Если уже админ - не нужна верификация
    if (isAdmin) {

      return { isVerified: true, isAdmin: true, passportState: 'admin' };
    }

    if (!phone) {
      setVerificationStatus(prev => ({
        ...prev,
        isLoading: false,
        isVerified: false,
      }));
      return;
    }

    try {
      logger.log('🔍 Проверка верификации для:', phone);
      setVerificationStatus(prev => ({ ...prev, isLoading: true }));
      
      const response = await makeAuthenticatedRequest(
        `${API_URL}/api/altyn/verification/status?phone=${encodeURIComponent(phone)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        logger.log('✅ Статус верификации:', data);
        
        // Проверяем если бэкенд вернул что это админ
        if (data.isAdmin) {
          setIsAdmin(true);
          setVerificationStatus({
            isLoading: false,
            isVerified: true,
            isAdmin: true,
            identificationLevel: 999,
            passportState: 'admin',
            phone: phone,
            accountNumber: data.accountNumber,
          });
          return data;
        }
        
        const kycMap = { pending_review: 'processing', rejected: 'failed', approved: 'success' };
        const resolvedPassportState = kycMap[data.kycReviewStatus] || data.passportState || 'not_registered';
        setVerificationStatus({
          isLoading: false,
          isVerified: data.isVerified || false,
          isAdmin: false,
          identificationLevel: data.identificationLevel || 0,
          passportState: resolvedPassportState,
          phone: phone,
          accountNumber: data.accountNumber,
          error: data.error,
        });
        
        // Сохраняем телефон в localStorage если верифицирован
        if (data.isVerified) {
          localStorage.setItem('userPhone', phone);
        }
        
        return data;
      } else {
        const errorData = await response.json().catch(() => ({}));
        logger.error('❌ Ошибка проверки верификации:', errorData);
        setVerificationStatus(prev => ({
          ...prev,
          isLoading: false,
          isVerified: false,
          error: errorData.detail || 'Ошибка проверки',
        }));
      }
    } catch (error) {
      logger.error('❌ Ошибка проверки верификации:', error);
      setVerificationStatus(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  }, [API_URL, makeAuthenticatedRequest, isAdmin]);

  // ========== ALTYN: UPLOAD DOCUMENT (ШАГ 1) ==========
  const uploadDocument = useCallback(async (file) => {
    try {
      logger.log('📤 Загрузка документа:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await makeAuthenticatedRequest(`${API_URL}/api/altyn/upload`, {
        method: 'POST',
        body: formData,
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        logger.log('✅ Документ загружен:', data.fileKey);
        return { success: true, fileKey: data.fileKey };
      } else {
        throw new Error(data.message || data.detail?.message || 'Ошибка загрузки');
      }
    } catch (error) {
      logger.error('❌ Ошибка загрузки документа:', error);
      throw error;
    }
  }, [API_URL, makeAuthenticatedRequest]);

  // ========== ALTYN: REGISTER CLIENT (ШАГ 2) ==========
  const registerClient = useCallback(async (registrationData) => {
    try {
      logger.log('📝 Регистрация клиента:', registrationData.phone);
      
      // Новый endpoint: /api/altyn/client/register
      const response = await makeAuthenticatedRequest(`${API_URL}/api/altyn/client/register`, {
        method: 'POST',
        body: JSON.stringify({
          phone: registrationData.phone,
          selfie_url: registrationData.selfie_url,
          document_url: registrationData.document_url,
          address_url: registrationData.address_url,
          passport_type: registrationData.passport_type || 'ru',
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        logger.log('✅ Клиент зарегистрирован в Altyn');
        return { success: true, ...data };
      } else {
        throw new Error(data.message || data.detail?.message || 'Ошибка регистрации');
      }
    } catch (error) {
      logger.error('❌ Ошибка регистрации:', error);
      throw error;
    }
  }, [API_URL, makeAuthenticatedRequest]);

  // ========== ALTYN: SEND VERIFICATION CODE (ШАГ 3) ==========
  const sendVerificationCode = useCallback(async (phone) => {
    try {
      logger.log('📱 Отправка кода на:', phone);
      
      const response = await makeAuthenticatedRequest(`${API_URL}/api/altyn/verification/send-code`, {
        method: 'POST',
        body: JSON.stringify({ phone }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        logger.log('✅ Код отправлен:', data.sendAs);
        return { success: true, sendAs: data.sendAs };
      } else {
        throw new Error(data.message || data.detail?.message || 'Ошибка отправки кода');
      }
    } catch (error) {
      logger.error('❌ Ошибка отправки кода:', error);
      throw error;
    }
  }, [API_URL, makeAuthenticatedRequest]);

  // ========== ALTYN: CONFIRM VERIFICATION (ШАГ 4) ==========
  const confirmVerification = useCallback(async (confirmData) => {
    try {
      logger.log('✅ Подтверждение верификации:', confirmData.phone);
      
      const response = await makeAuthenticatedRequest(`${API_URL}/api/altyn/verification/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          phone: confirmData.phone,
          verification_code: confirmData.verification_code,
          selfie_url: confirmData.selfie_url,
          document_url: confirmData.document_url,
          address_url: confirmData.address_url,
          passport_type: confirmData.passport_type || 'ru',
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        logger.log('✅ Верификация подтверждена!');
        // Обновляем статус верификации
        await checkVerificationStatus(confirmData.phone);
        return { success: true, ...data };
      } else {
        throw new Error(data.message || data.detail?.message || 'Ошибка подтверждения');
      }
    } catch (error) {
      logger.error('❌ Ошибка подтверждения:', error);
      throw error;
    }
  }, [API_URL, makeAuthenticatedRequest, checkVerificationStatus]);

  // ========== ALTYN: GET SBP BANKS ==========
  const loadSbpBanks = useCallback(async () => {
    try {
      logger.log('🏦 Загрузка списка банков СБП...');
      
      const response = await fetch(`${API_URL}/api/altyn/banks`, {
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.banks) {
          setSbpBanks(data.banks);
          logger.log('✅ Загружено банков:', data.banks.length);
        }
      }
    } catch (error) {
      logger.error('❌ Ошибка загрузки банков:', error);
      // Fallback на статический список
      setSbpBanks([
        { id: 'tinkoff', name: 'Т-Банк' },
        { id: 'sber', name: 'Сбербанк' },
        { id: 'vtb', name: 'ВТБ' },
        { id: 'alfa', name: 'Альфа Банк' },
        { id: 'raif', name: 'Райффайзен Банк' },
        { id: 'gazprom', name: 'Газпромбанк' },
      ]);
    }
  }, [API_URL]);

  // Функция создания сессии пользователя
  const createUserSession = useCallback(async (telegramApp, telegramUserId) => {
    if (!telegramApp || !telegramUserId) return null;
    
    try {
      const initData = telegramApp.initData;
      
      const requestData = {
        initData: initData || '',
        userAgent: navigator.userAgent,
        platform: telegramApp.platform || '',
        version: telegramApp.version || '',
        timestamp: Date.now()
      };
      
      logger.log('Создание сессии с данными:', { 
        hasInitData: !!initData,
        userId: telegramUserId 
      });
      
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Ошибка создания сессии:', errorData);
        throw new Error(errorData.detail?.message || errorData.message || 'Ошибка создания сессии');
      }
      
      const data = await response.json();
      
      if (data.success && data.sessionToken) {
        localStorage.setItem('sessionToken', data.sessionToken);
        localStorage.setItem('userId', String(data.userId));
        localStorage.setItem('sessionExpiry', data.expiresAt);
        logger.log('✅ Сессия создана успешно для пользователя:', data.userId);
        return data.sessionToken;
      } else {
        throw new Error(data.message || 'Ошибка создания сессии');
      }
    } catch (error) {
      logger.error('❌ Ошибка создания сессии:', error);
      if (tg) {
        tg.showAlert('Ошибка авторизации: ' + error.message);
      }
      return null;
    }
  }, [API_URL, tg]);

  // Проверка валидности сессии
  const checkSessionValidity = useCallback(async () => {
    const sessionToken = localStorage.getItem('sessionToken');
    const storedUserId = localStorage.getItem('userId');
    
    if (!sessionToken || !storedUserId) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          sessionToken: sessionToken,
          userId: parseInt(storedUserId)
        })
      });
      
      if (!response.ok) {
        logger.error('Ошибка проверки сессии, статус:', response.status);
        return false;
      }
      
      const data = await response.json();
      
      if (!data.success || !data.valid) {
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('sessionExpiry');
        logger.log('⚠️ Сессия недействительна');
        return false;
      }
      
      logger.log('✅ Сессия действительна');
      return true;
    } catch (error) {
      logger.error('❌ Ошибка проверки сессии:', error);
      return false;
    }
  }, [API_URL]);

  // Функция загрузки курсов валют
  const fetchExchangeRates = useCallback(async () => {
    try {
      logger.log('📊 Загрузка курсов валют...');
      
      const response = await fetch(`${API_URL}/api/rates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        }
      });
      
      if (!response.ok) {
        logger.error('❌ Ошибка загрузки курсов, статус:', response.status);
        return;
      }
      
      const data = await response.json();
      
      if (data.rubToCny && data.usdtToCny) {
        setExchangeRate(data.rubToCny);
        setUsdtToCnyRate(data.usdtToCny);
        logger.log('✅ Курсы обновлены:', {
          rubToCny: data.rubToCny,
          usdtToCny: data.usdtToCny,
          lastUpdated: data.lastUpdated
        });
      }
    } catch (error) {
      logger.error('❌ Ошибка загрузки курсов:', error);
    }
  }, [API_URL]);

  // Функция динамического расчета суммы к получению в CNY
  // inputAmount - сумма, введённая пользователем (в рублях для RUB методов, в USDT для USDT)
  // type - тип заявки (alipay, wechat)
  // paymentMethod - метод оплаты (RUB_SBP, RUB_VTB, USDT)
  const calculateAmount = useCallback(async (inputAmount, type = 'alipay', paymentMethod = 'RUB_SBP') => {
    if (!inputAmount || parseFloat(inputAmount) <= 0) {
      setCalculatedAmounts(prev => ({
        ...prev,
        [type]: { cny: null, loading: false }
      }));
      return;
    }

    // Устанавливаем loading
    setCalculatedAmounts(prev => ({
      ...prev,
      [type]: { ...prev[type], loading: true }
    }));

    try {
      const amount = parseFloat(inputAmount);
      const isUsdt = paymentMethod === 'USDT';
      
      logger.log(`📊 Расчет суммы для ${amount} ${isUsdt ? 'USDT' : 'RUB'}...`);
      
      // Запрашиваем актуальный курс
      const response = await fetch(`${API_URL}/api/rates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка получения курса');
      }
      
      const data = await response.json();
      
      // Обновляем глобальные курсы
      if (data.rubToCny) setExchangeRate(data.rubToCny);
      if (data.usdtToCny) setUsdtToCnyRate(data.usdtToCny);
      
      let cnyAmount;
      if (isUsdt) {
        // Для USDT: USDT * курс USDT/CNY = юани
        cnyAmount = data.usdtToCny ? (amount * data.usdtToCny).toFixed(2) : null;
        logger.log(`✅ Расчет: ${amount} USDT = ${cnyAmount} CNY (курс: ${data.usdtToCny})`);
      } else {
        // Для рублей: рубли / курс RUB/CNY = юани
        cnyAmount = data.rubToCny ? (amount / data.rubToCny).toFixed(2) : null;
        logger.log(`✅ Расчет: ${amount} ₽ = ${cnyAmount} CNY (курс: ${data.rubToCny})`);
      }
      
      setCalculatedAmounts(prev => ({
        ...prev,
        [type]: { cny: cnyAmount, loading: false }
      }));
      
      return { cny: cnyAmount, rate: isUsdt ? data.usdtToCny : data.rubToCny };
    } catch (error) {
      logger.error('❌ Ошибка расчета:', error);
      setCalculatedAmounts(prev => ({
        ...prev,
        [type]: { cny: null, loading: false }
      }));
    }
  }, [API_URL]);

  // Функция загрузки лимитов
  const fetchLimits = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/limits`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        }
      });
      
      if (!response.ok) return;
      
      const data = await response.json();
      
      if (data.minAmount && data.maxAmount) {
        setLimits({
          minAmount: data.minAmount,
          maxAmount: data.maxAmount,
          currency: data.currency || 'CNY'
        });
      }
    } catch (error) {
      console.warn('⚠️ Ошибка загрузки лимитов:', error);
    }
  }, [API_URL]);

  // ========== FETCH PAYMENT LIMITS (СБП/ВТБ) ==========
  const fetchPaymentLimits = useCallback(async () => {
    try {
      logger.log('📊 Загрузка лимитов...');
      const response = await makeAuthenticatedRequest(`${API_URL}/api/user/limits`);
      
      if (!response.ok) {
        console.warn('⚠️ Ошибка загрузки лимитов, статус:', response.status);
        return;
      }
      
      const data = await response.json();
      logger.log('📊 Ответ API лимитов:', data);
      
      if (data.success) {
        setPaymentLimits(prev => ({
          sbp: data.sbp || prev.sbp,
          vtb: data.vtb || prev.vtb,
          currency: data.currency || 'RUB',
        }));
        logger.log('✅ Лимиты загружены:', data);
      } else {
        console.warn('⚠️ API вернул success: false', data);
      }
    } catch (error) {
      console.warn('⚠️ Ошибка загрузки лимитов оплаты:', error);
    }
  }, [API_URL, makeAuthenticatedRequest]);

  // Загрузка фото профиля пользователя
  const loadUserPhoto = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_URL}/api/user/photo`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.photoUrl) {
          setUserPhotoUrl(data.photoUrl);
        }
      }
    } catch (error) {
      logger.error('❌ Ошибка загрузки фото профиля:', error);
    }
  }, [API_URL, makeAuthenticatedRequest]);

  // Загрузка данных пользователя
  const loadUserData = useCallback(async () => {
    try {
      logger.log('📥 Загрузка данных пользователя...');
      
      // Загружаем заявки
      const requestsResponse = await makeAuthenticatedRequest(`${API_URL}/api/user/requests`);
      
      if (requestsResponse.ok) {
        const requestsData = await requestsResponse.json();
        
        if (requestsData.requests && Array.isArray(requestsData.requests)) {
          const formattedRequests = requestsData.requests.map(req => {
            let formattedDate = '';
            const dateField = req.created_at || req.createdAt || req.created || req.date;
            if (dateField) {
              try {
                const dateObj = new Date(dateField);
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                formattedDate = `${day}.${month}.${year}`;
              } catch (e) {
                formattedDate = '';
              }
            }
            
            return {
              id: req.order_id || req.orderId || req.id,
              type: req.type === 'alipay' ? 'Alipay' : 'WeChat',
              amount: req.amount,
              status: req.status,
              date: formattedDate,
              paymentMethod: req.payment_method || req.paymentMethod || '',
              bank: req.bank || '',
              network: req.network || '',
              fullName: req.full_name || req.fullName || '',
              paymentDetails: req.payment_details || req.paymentDetails,
            };
          });
          setRequests(formattedRequests);
        }
      }
      
      // Загружаем историю
      const historyResponse = await makeAuthenticatedRequest(`${API_URL}/api/user/history`);
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        if (historyData.history && Array.isArray(historyData.history)) {
          const formattedHistory = historyData.history.map(item => {
            let formattedDate = '';
            const dateField = item.created_at || item.createdAt || item.created || item.date;
            if (dateField) {
              try {
                const dateObj = new Date(dateField);
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                formattedDate = `${day}.${month}.${year}`;
              } catch (e) {
                formattedDate = '';
              }
            }
            
            return {
              id: item.order_id || item.orderId || item.id,
              type: item.type === 'alipay' ? 'Alipay' : 'WeChat',
              amount: item.amount,
              date: formattedDate,
              paymentMethod: item.payment_method || item.paymentMethod,
              rub: (item.payment_method || item.paymentMethod) === 'RUB' && exchangeRate ? item.amount * exchangeRate : 0
            };
          });
          setHistory(formattedHistory);
        }
      }
      
      // Загружаем статистику
      const statsResponse = await makeAuthenticatedRequest(`${API_URL}/api/user/stats`);
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData) {
          setStats({
            totalRequests: statsData.totalRequests || 0,
            totalAmountCNY: statsData.totalAmountCNY || 0,
            totalAmountRUB: statsData.totalAmountRUB || 0,
            completedRequests: statsData.completedRequests || 0,
            pendingRequests: statsData.pendingRequests || 0
          });
        }
      }
      
    } catch (error) {
      logger.error('❌ Ошибка загрузки данных:', error);
    }
  }, [API_URL, exchangeRate, makeAuthenticatedRequest]);

  // Инициализация Telegram Web App
  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    // Отправка диагностики на бэкенд при сбое инициализации.
    // Собираем максимум безопасной информации, чтобы понимать какие устройства
    // /версии/сети падают (без PII).
    const sendInitDiagnostics = (reason, extra = {}) => {
      try {
        const tg = window.Telegram?.WebApp;
        const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const payload = {
          reason, // 'sdk_error' | 'sdk_missing' | 'empty_init_data' | 'no_user_id' | 'init_exception'
          at: Date.now(),
          ua: navigator.userAgent,
          lang: navigator.language,
          languages: navigator.languages,
          platform: navigator.platform,
          vendor: navigator.vendor,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
          doNotTrack: navigator.doNotTrack,
          hardwareConcurrency: navigator.hardwareConcurrency,
          deviceMemory: navigator.deviceMemory,
          maxTouchPoints: navigator.maxTouchPoints,
          screen: {
            w: window.screen?.width,
            h: window.screen?.height,
            availW: window.screen?.availWidth,
            availH: window.screen?.availHeight,
            dpr: window.devicePixelRatio,
            orientation: window.screen?.orientation?.type,
            colorDepth: window.screen?.colorDepth,
          },
          viewport: { w: window.innerWidth, h: window.innerHeight },
          connection: conn ? {
            effectiveType: conn.effectiveType,
            downlink: conn.downlink,
            rtt: conn.rtt,
            saveData: conn.saveData,
            type: conn.type,
          } : null,
          sdkStatus: window.__TG_SDK_STATUS__,
          sdkSource: window.__TG_SDK_SOURCE__, // 'local' | 'remote' | null
          sdkLocalFailed: !!window.__TG_SDK_LOCAL_FAILED__,
          hasTelegramObject: !!window.Telegram,
          hasWebApp: !!tg,
          tg: tg ? {
            platform: tg.platform,
            version: tg.version,
            colorScheme: tg.colorScheme,
            isExpanded: tg.isExpanded,
            viewportHeight: tg.viewportHeight,
            viewportStableHeight: tg.viewportStableHeight,
            hasInitData: !!tg.initData,
            initDataLen: (tg.initData || '').length,
            hasUser: !!tg.initDataUnsafe?.user,
            authDate: tg.initDataUnsafe?.auth_date,
            chatType: tg.initDataUnsafe?.chat_type,
            startParam: tg.initDataUnsafe?.start_param,
          } : null,
          location: {
            host: window.location.host,
            pathname: window.location.pathname,
            protocol: window.location.protocol,
          },
          referrer: document.referrer,
          inIframe: (() => { try { return window.self !== window.top; } catch (_) { return true; } })(),
          timing: (() => {
            try {
              const nav = performance.getEntriesByType?.('navigation')?.[0];
              return nav ? {
                dns: nav.domainLookupEnd - nav.domainLookupStart,
                tcp: nav.connectEnd - nav.connectStart,
                tls: nav.secureConnectionStart > 0 ? nav.connectEnd - nav.secureConnectionStart : null,
                ttfb: nav.responseStart - nav.requestStart,
                domLoaded: nav.domContentLoadedEventEnd - nav.startTime,
                loadEvent: nav.loadEventEnd - nav.startTime,
                transferSize: nav.transferSize,
              } : null;
            } catch (_) { return null; }
          })(),
          ...extra,
        };

        const body = JSON.stringify(payload);
        const url = `${API_URL}/api/diagnostics/init-failure`;
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true })
            .catch(() => {});
        }
      } catch (_) {
        // Диагностика не должна никогда ронять приложение.
      }
    };

    // Ждём появления window.Telegram.WebApp. SDK (telegram-web-app.js) может
    // загрузиться с задержкой (медленный LTE, VPN, DPI, ретраи из index.html:
    // локальный → fallback telegram.org → retry x3), поэтому даём ему до ~15 с
    // прежде чем показывать ошибку.
    const waitForWebApp = (timeoutMs = 15000, intervalMs = 150) => {
      return new Promise((resolve) => {
        const started = Date.now();
        const tick = () => {
          if (cancelled) return resolve(null);
          if (window.Telegram?.WebApp) return resolve(window.Telegram.WebApp);
          if (window.__TG_SDK_STATUS__ === 'error' && Date.now() - started > 3000) {
            // Явная ошибка загрузки SDK + дали шанс последней попытке — хватит ждать.
            return resolve(null);
          }
          if (Date.now() - started >= timeoutMs) return resolve(null);
          pollTimer = setTimeout(tick, intervalMs);
        };
        tick();
      });
    };

    const init = async () => {
      try {
        logger.log('=== Инициализация Telegram WebApp ===');

        const telegram = await waitForWebApp();
        if (cancelled) return;

        if (telegram) {
          logger.log('✅ Telegram WebApp найден');

          telegram.ready();
          telegram.expand();
          try { telegram.enableClosingConfirmation(); } catch (_) { /* старая версия TG */ }

          const initData = telegram.initData;
          const user = telegram.initDataUnsafe?.user;

          if (!initData) {
            logger.error('⚠️ КРИТИЧНО: initData пустой!');
            sendInitDiagnostics('empty_init_data');
            setInitError('Пожалуйста, откройте приложение через бота в Telegram');
            return;
          }

          if (!user?.id) {
            logger.error('⚠️ КРИТИЧНО: User ID не найден!');
            sendInitDiagnostics('no_user_id');
            setInitError('Ошибка получения данных пользователя');
            return;
          }

          setTg(telegram);
          setUserId(user.id);
          setTelegramUsername(user.username || '');
          setUserFirstName(user.first_name || '');
          setUserLastName(user.last_name || '');

          document.body.style.backgroundColor = telegram.backgroundColor;
          setIsAppReady(true);

          logger.log('✅ Инициализация завершена');
          return;
        }

        logger.error('❌ Telegram WebApp недоступен');

        const isDevelopment = window.location.hostname === 'localhost' ||
                             window.location.hostname === '127.0.0.1';

        if (isDevelopment) {
          logger.log('🔧 DEV MODE: создаем mock объект');

          const mockTelegram = {
            ready: () => {},
            expand: () => {},
            enableClosingConfirmation: () => {},
            BackButton: {
              show: () => {},
              hide: () => {},
              onClick: () => {},
              offClick: () => {}
            },
            initData: '',
            initDataUnsafe: {
              user: {
                id: 123456789,
                first_name: 'Test',
                last_name: 'User',
                username: 'testuser'
              }
            },
            backgroundColor: '#ffffff',
            showAlert: (msg) => alert(msg),
            close: () => {},
            platform: 'unknown',
            version: '6.0'
          };

          setTg(mockTelegram);
          setUserId(123456789);
          setTelegramUsername('testuser');
          setUserFirstName('Test');
          setUserLastName('User');
          setIsAppReady(true);

          alert('⚠️ DEV MODE: Используются моковые данные.');
        } else if (window.__TG_SDK_STATUS__ === 'error') {
          // SDK не загрузился — почти наверняка сеть/DPI/провайдер/VPN.
          sendInitDiagnostics('sdk_error');
          setInitError('Не удалось загрузить Telegram SDK. Проверьте интернет и откройте приложение заново через бота.');
        } else {
          sendInitDiagnostics('sdk_missing');
          setInitError('Это приложение должно открываться через Telegram Bot');
        }
      } catch (err) {
        logger.error('Ошибка инициализации:', err);
        sendInitDiagnostics('init_exception', { error: String(err?.message || err), stack: err?.stack });
        setInitError('Ошибка запуска приложения. Попробуйте обновить Telegram.');
      }
    };

    init();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, []);

  // Загрузка курсов, лимитов и банков при открытии приложения
  useEffect(() => {
    fetchExchangeRates();
    fetchLimits();
    loadSbpBanks();
    
    const ratesInterval = setInterval(fetchExchangeRates, 5 * 60 * 1000);
    const limitsInterval = setInterval(fetchLimits, 30 * 60 * 1000);
    
    return () => {
      clearInterval(ratesInterval);
      clearInterval(limitsInterval);
    };
  }, [fetchExchangeRates, fetchLimits, loadSbpBanks]);

  // Проверка сохранённого телефона и статуса верификации при загрузке
  useEffect(() => {
    const savedPhone = localStorage.getItem('userPhone');
    if (savedPhone) {
      setUserPhone(savedPhone);
    }
  }, []);

  // Проверка верификации при изменении телефона
  useEffect(() => {
    if (userPhone && localStorage.getItem('sessionToken')) {
      checkVerificationStatus(userPhone);
    }
  }, [userPhone, checkVerificationStatus]);

  // Создание сессии после инициализации Telegram
  // Функция для проверки статуса после авторизации (сначала админ, потом верификация)
  const checkUserStatus = useCallback(async () => {
    // Загружаем лимиты оплаты
    fetchPaymentLimits();
    
    // Сначала проверяем админа
    const adminResult = await checkAdminStatus();
    
    if (adminResult.isAdmin) {
      logger.log('👑 Пользователь - админ, верификация не требуется');
      return;
    }
    
    // Проверяем верификацию через новый endpoint (по user ID из токена)
    try {
      logger.log('🔍 Проверка верификации пользователя...');
      const response = await makeAuthenticatedRequest(`${API_URL}/api/user/verification`);
      
      if (response.ok) {
        const data = await response.json();
        logger.log('✅ Статус верификации:', data);
        
        if (data.isVerified) {
          // Пользователь верифицирован - сохраняем данные
          if (data.phone) {
            setUserPhone(data.phone);
            localStorage.setItem('userPhone', data.phone);
          }
          
          setVerificationStatus({
            isLoading: false,
            isVerified: true,
            isAdmin: false,
            identificationLevel: data.identificationLevel || 1,
            passportState: data.passportState || 'success',
            phone: data.phone,
            accountNumber: data.accountNumber,
          });
          
          logger.log('✅ Пользователь верифицирован, можно создавать депозиты');
        } else if (data.phone && (data.passportState === 'processing' || data.kycReviewStatus === 'pending_review')) {
          // Верификация в процессе
          setUserPhone(data.phone);
          localStorage.setItem('userPhone', data.phone);

          setVerificationStatus({
            isLoading: false,
            isVerified: false,
            isAdmin: false,
            identificationLevel: data.identificationLevel || 0,
            passportState: 'processing',
            phone: data.phone,
          });

          logger.log('⏳ Верификация в процессе проверки');
        } else {
          // Не верифицирован - нужна KYC форма
          const kycMap = { pending_review: 'processing', rejected: 'failed', approved: 'success' };
          setVerificationStatus({
            isLoading: false,
            isVerified: false,
            isAdmin: false,
            identificationLevel: 0,
            passportState: kycMap[data.kycReviewStatus] || data.passportState || 'not_registered',
            phone: data.phone || null,
          });

          logger.log('⚠️ Требуется верификация');
        }
        
        return;
      }
    } catch (error) {
      logger.log('ℹ️ Новый endpoint недоступен, пробуем старый метод:', error.message);
    }
    
    // Fallback: старый метод проверки по телефону из localStorage
    const savedPhone = localStorage.getItem('userPhone');
    if (savedPhone) {
      await checkVerificationStatus(savedPhone);
    } else {
      setVerificationStatus(prev => ({ ...prev, isLoading: false }));
    }
  }, [API_URL, checkAdminStatus, checkVerificationStatus, fetchPaymentLimits, makeAuthenticatedRequest]);

  useEffect(() => {
    if (tg && userId) {
      const existingToken = localStorage.getItem('sessionToken');
      const existingUserId = localStorage.getItem('userId');
      
      if (existingToken && existingUserId === String(userId)) {
        checkSessionValidity().then(isValid => {
          if (isValid) {
            logger.log('Сессия действительна, загружаем данные');
            loadUserData();
            loadUserPhoto();
            checkUserStatus(); // Проверяем админ + верификацию
          } else {
            logger.log('Создание новой сессии');
            createUserSession(tg, userId).then(sessionToken => {
              if (sessionToken) {
                loadUserData();
                loadUserPhoto();
                checkUserStatus(); // Проверяем админ + верификацию
              } else {
                if (tg) {
                  tg.showAlert('Ошибка авторизации. Попробуйте перезапустить приложение.');
                }
              }
            });
          }
        });
      } else {
        logger.log('Создание новой сессии');
        createUserSession(tg, userId).then(sessionToken => {
          if (sessionToken) {
            loadUserData();
            loadUserPhoto();
            checkUserStatus(); // Проверяем админ + верификацию
          } else {
            if (tg) {
              tg.showAlert('Ошибка авторизации. Попробуйте перезапустить приложение.');
            }
          }
        });
      }

      const sessionCheckInterval = setInterval(async () => {
        const isValid = await checkSessionValidity();
        if (!isValid && tg) {
          tg.showAlert('Сессия истекла. Приложение будет перезапущено.');
          setTimeout(() => {
            tg.close();
          }, 2000);
        }
      }, 30 * 60 * 1000);

      return () => clearInterval(sessionCheckInterval);
    }
  }, [tg, userId, createUserSession, checkSessionValidity, loadUserData, loadUserPhoto, checkUserStatus]);

  // ========== GLOBAL WEBSOCKET (tracking + push-события) ==========
  const globalWsRef = useRef(null);
  const globalWsPingRef = useRef(null);
  const globalWsReconnectRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    const connectWS = () => {
      const ws = new WebSocket(`wss://api.kit8exchange.com/ws/user/${userId}`);
      globalWsRef.current = ws;

      ws.onopen = () => {
        logger.log('Global WS connected');
        setTrackerWS(ws);
        globalWsPingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
      };

      ws.onmessage = (e) => {
        if (e.data === 'pong') return;

        try {
          const msg = JSON.parse(e.data);

          switch (msg.type) {
            case 'track_ok':
              logger.log('Track OK:', msg.event);
              break;
            case 'track_error':
              logger.warn('Track error:', msg);
              break;
            case 'connected':
              logger.log('WS connected:', msg);
              break;
            case 'kyc_status':
              logger.log('KYC status:', msg);
              break;
            case 'status_update':
              logger.log('Status update:', msg);
              break;
            default:
              break;
          }
        } catch (err) {
          logger.error('WS parse error:', err);
        }
      };

      ws.onclose = () => {
        logger.log('Global WS closed');
        setTrackerWS(null);
        clearInterval(globalWsPingRef.current);
        globalWsPingRef.current = null;
        globalWsReconnectRef.current = setTimeout(connectWS, 3000);
      };

      ws.onerror = () => ws.close();
    };

    connectWS();

    return () => {
      clearTimeout(globalWsReconnectRef.current);
      clearInterval(globalWsPingRef.current);
      if (globalWsRef.current) {
        globalWsRef.current.close(1000, 'App unmount');
      }
      setTrackerWS(null);
    };
  }, [userId]);

  // Управление кнопкой "Назад" Telegram
  useEffect(() => {
    if (tg && tg.BackButton) {
      if (currentScreen !== 'main') {
        tg.BackButton.show();
        
        const handleBackClick = () => {
          // Если мы в профиле и в какой-то секции - сначала выходим из секции
          if (currentScreen === 'profile' && profileActiveSection !== null) {
            setProfileActiveSection(null);
          } else {
            navigateTo('main');
          }
        };
        
        tg.BackButton.onClick(handleBackClick);
        
        return () => {
          tg.BackButton.offClick(handleBackClick);
          if (currentScreen === 'main') {
            tg.BackButton.hide();
          }
        };
      } else {
        tg.BackButton.hide();
      }
    }
  }, [tg, currentScreen, navigateTo, profileActiveSection]);

  // Автоподгрузка данных при открытии профиля
  useEffect(() => {
    if (currentScreen === 'profile' && userId) {
      const sessionToken = localStorage.getItem('sessionToken');
      if (sessionToken) {
        loadUserData();
        loadUserPhoto();
        fetchPaymentLimits(); // Обновляем лимиты при открытии профиля
      }
    }
  }, [currentScreen, userId, loadUserData, loadUserPhoto, fetchPaymentLimits]);

  // ========== DEPOSIT HANDLERS ==========
  const handleAlipayDeposit = async () => {
    // Проверка верификации
    if (!verificationStatus.isVerified) {
      if (tg) {
        tg.showAlert('Для создания заявки необходимо пройти KYC верификацию в личном кабинете');
      } else {
        alert('Для создания заявки необходимо пройти KYC верификацию в личном кабинете');
      }
      navigateTo('profile');
      return;
    }

    // Проверка лимитов суммы
    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      if (tg) {
        tg.showAlert('Введите сумму');
      }
      return;
    }
    
    if (amountValue > limits.maxAmount) {
      if (tg) {
        tg.showAlert(`Максимальная сумма: ${limits.maxAmount.toLocaleString()} ${limits.currency}`);
      }
      return;
    }
    
    const isRubSbpValid = paymentMethod === 'RUB_SBP' && selectedBank;
    const isRubVtbValid = paymentMethod === 'RUB_VTB'; // VTB не требует выбора банка
    const isUsdtValid = paymentMethod === 'USDT' && usdtNetwork;
    const isRubValid = isRubSbpValid || isRubVtbValid;
    
    if (!paymentMethod || !amount || !fullName || !qrCode || (!isRubValid && !isUsdtValid)) {
      if (tg) {
        tg.showAlert('Пожалуйста, заполните все поля и загрузите QR код');
      }
      return;
    }

    setIsSubmitting(true);
    track('deposit_submitted', { amount: parseFloat(amount), method: paymentMethod, type: 'alipay' });

    try {
      logger.log('📤 Создание заявки Alipay...');
      
      // Конвертируем введённую сумму в юани для отправки на сервер
      const inputAmount = parseFloat(amount);
      let cnyAmountToSend;
      if (paymentMethod === 'USDT') {
        // USDT * курс USDT/CNY = юани
        cnyAmountToSend = calculatedAmounts.alipay?.cny 
          ? parseFloat(calculatedAmounts.alipay.cny) 
          : (inputAmount * (usdtToCnyRate || 7.2));
      } else {
        // Рубли / курс RUB/CNY = юани
        cnyAmountToSend = calculatedAmounts.alipay?.cny 
          ? parseFloat(calculatedAmounts.alipay.cny) 
          : (inputAmount / (exchangeRate || 11.74));
      }
      
      logger.log(`💱 Конвертация: ${inputAmount} ${paymentMethod === 'USDT' ? 'USDT' : 'RUB'} → ${cnyAmountToSend.toFixed(2)} CNY`);
      
      const formData = new FormData();
      formData.append('phone', userPhone); // Верифицированный телефон
      formData.append('paymentMethod', paymentMethod);
      formData.append('amount', cnyAmountToSend.toFixed(2)); // Отправляем сумму в юанях
      formData.append('fullName', fullName);
      formData.append('qrCode', qrCode);
      formData.append('type', 'alipay');
      if (selectedBank) formData.append('bank', selectedBank);
      if (usdtNetwork) formData.append('network', usdtNetwork);
      if (telegramUsername) formData.append('telegramUsername', telegramUsername);
      if (paymentMethod === 'USDT') formData.append('amountUsdt', inputAmount.toString());

      const response = await makeAuthenticatedRequest(`${API_URL}/api/deposit`, {
        method: 'POST',
        body: formData
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        logger.error('❌ Сервер вернул не JSON:', text.substring(0, 200));
        throw new Error('Сервер вернул некорректный ответ.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Специальная обработка ошибок верификации
        if (errorData.error === 'NOT_VERIFIED' || errorData.error === 'NOT_REGISTERED') {
          track('error_displayed', { error: errorData.error, page: 'deposit' });
          if (tg) {
            tg.showAlert(errorData.message || 'Необходимо пройти KYC верификацию');
          }
          navigateTo('profile');
          return;
        }

        // Обработка ошибок лимитов
        if (errorData.error === 'AMOUNT_TOO_LOW') {
          track('error_displayed', { error: 'AMOUNT_TOO_LOW', page: 'deposit' });
          const minAmount = errorData.minAmount ? errorData.minAmount.toLocaleString() : '2000';
          if (tg) {
            tg.showAlert(`Минимальная сумма: ${minAmount} ₽`);
          }
          return;
        }

        if (errorData.error === 'LIMIT_EXCEEDED') {
          track('error_displayed', { error: 'LIMIT_EXCEEDED', page: 'deposit' });
          const maxAllowed = errorData.maxAllowed ? errorData.maxAllowed.toLocaleString() : '—';
          if (tg) {
            tg.showAlert(`Превышен лимит. Доступно: ${maxAllowed} ₽`);
          }
          return;
        }

        // Обработка ошибок от платёжной системы (PAYMENT_ERROR с detail строкой)
        if (errorData.error === 'PAYMENT_ERROR' && errorData.detail) {
          track('error_displayed', { error: 'PAYMENT_ERROR', page: 'deposit' });
          const detailMsg = typeof errorData.detail === 'string' ? errorData.detail : errorData.detail.message;
          if (detailMsg) {
            if (tg) {
              tg.showAlert(detailMsg);
            }
            return;
          }
        }

        // Общая обработка: извлекаем понятное сообщение из detail
        const errorMessage = (typeof errorData.detail === 'string' ? errorData.detail : null)
          || errorData.detail?.message
          || (errorData.message !== 'Validation error' ? errorData.message : null)
          || 'Ошибка создания заявки';

        throw new Error(errorMessage);
      }

      const data = await response.json();

      const actualOrderId = data.orderId || data.order_id;

      if (data.success && actualOrderId) {
        logger.log('✅ Заявка Alipay создана:', actualOrderId);
        track('deposit_created', { orderId: actualOrderId, type: 'alipay', amount: cnyAmountToSend.toFixed(2), method: paymentMethod });
        
        const orderData = {
          orderId: actualOrderId,
          externalId: data.externalId,
          altynPaymentId: data.altynPaymentId,
          type: 'Alipay',
          amount: cnyAmountToSend.toFixed(2), // Сумма в юанях (уже рассчитанная)
          inputAmount: amount, // Введённая сумма (RUB или USDT)
          inputCurrency: paymentMethod === 'USDT' ? 'USDT' : 'RUB',
          paymentMethod: paymentMethod,
          bank: selectedBank,
          network: usdtNetwork,
          fullName: fullName,
          paymentDetails: data.paymentDetails,
          status: data.status || 'pending'
        };
        setOrderDetails(orderData);
        
        triggerHaptic('success');
        setCurrentScreen('orderConfirmation');
        
        // Очищаем форму
        setPaymentMethod('');
        setAmount('');
        setFullName('');
        setQrCode(null);
        setSelectedBank('');
        setUsdtNetwork('');
        
        loadUserData();
      } else {
        throw new Error(data.message || 'Ошибка создания заявки');
      }
      
    } catch (error) {
      logger.error('Error:', error);
      if (tg) {
        tg.showAlert('Ошибка: ' + error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWechatDeposit = async () => {
    // Проверка верификации
    if (!verificationStatus.isVerified) {
      if (tg) {
        tg.showAlert('Для создания заявки необходимо пройти KYC верификацию в личном кабинете');
      }
      navigateTo('profile');
      return;
    }

    const amountValue = parseFloat(wechatAmount);
    if (isNaN(amountValue) || amountValue <= 0) {
      if (tg) {
        tg.showAlert('Введите сумму');
      }
      return;
    }
    
    if (amountValue > limits.maxAmount) {
      if (tg) {
        tg.showAlert(`Максимальная сумма: ${limits.maxAmount.toLocaleString()} ${limits.currency}`);
      }
      return;
    }
    
    const isRubSbpValid = wechatPaymentMethod === 'RUB_SBP' && wechatSelectedBank;
    const isRubVtbValid = wechatPaymentMethod === 'RUB_VTB'; // VTB не требует выбора банка
    const isUsdtValid = wechatPaymentMethod === 'USDT' && wechatUsdtNetwork;
    const isRubValid = isRubSbpValid || isRubVtbValid;
    
    if (!wechatPaymentMethod || !wechatAmount || !wechatFullName || !wechatQrCode || (!isRubValid && !isUsdtValid)) {
      if (tg) {
        tg.showAlert('Пожалуйста, заполните все поля и загрузите QR код');
      }
      return;
    }

    setIsSubmitting(true);
    track('deposit_submitted', { amount: parseFloat(wechatAmount), method: wechatPaymentMethod, type: 'wechat' });

    try {
      logger.log('📤 Создание заявки WeChat...');
      
      // Конвертируем введённую сумму в юани для отправки на сервер
      const inputAmount = parseFloat(wechatAmount);
      let cnyAmountToSend;
      if (wechatPaymentMethod === 'USDT') {
        // USDT * курс USDT/CNY = юани
        cnyAmountToSend = calculatedAmounts.wechat?.cny 
          ? parseFloat(calculatedAmounts.wechat.cny) 
          : (inputAmount * (usdtToCnyRate || 7.2));
      } else {
        // Рубли / курс RUB/CNY = юани
        cnyAmountToSend = calculatedAmounts.wechat?.cny 
          ? parseFloat(calculatedAmounts.wechat.cny) 
          : (inputAmount / (exchangeRate || 11.74));
      }
      
      logger.log(`💱 Конвертация: ${inputAmount} ${wechatPaymentMethod === 'USDT' ? 'USDT' : 'RUB'} → ${cnyAmountToSend.toFixed(2)} CNY`);
      
      const formData = new FormData();
      formData.append('phone', userPhone);
      formData.append('paymentMethod', wechatPaymentMethod);
      formData.append('amount', cnyAmountToSend.toFixed(2)); // Отправляем сумму в юанях
      formData.append('fullName', wechatFullName);
      formData.append('qrCode', wechatQrCode);
      formData.append('type', 'wechat');
      if (wechatSelectedBank) formData.append('bank', wechatSelectedBank);
      if (wechatUsdtNetwork) formData.append('network', wechatUsdtNetwork);
      if (telegramUsername) formData.append('telegramUsername', telegramUsername);
      if (wechatPaymentMethod === 'USDT') formData.append('amountUsdt', inputAmount.toString());

      const response = await makeAuthenticatedRequest(`${API_URL}/api/deposit`, {
        method: 'POST',
        body: formData
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Сервер вернул некорректный ответ.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (errorData.error === 'NOT_VERIFIED' || errorData.error === 'NOT_REGISTERED') {
          track('error_displayed', { error: errorData.error, page: 'wechat' });
          if (tg) {
            tg.showAlert(errorData.message || 'Необходимо пройти KYC верификацию');
          }
          navigateTo('profile');
          return;
        }

        // Обработка ошибок лимитов
        if (errorData.error === 'AMOUNT_TOO_LOW') {
          track('error_displayed', { error: 'AMOUNT_TOO_LOW', page: 'wechat' });
          const minAmount = errorData.minAmount ? errorData.minAmount.toLocaleString() : '2000';
          if (tg) {
            tg.showAlert(`Минимальная сумма: ${minAmount} ₽`);
          }
          return;
        }

        if (errorData.error === 'LIMIT_EXCEEDED') {
          track('error_displayed', { error: 'LIMIT_EXCEEDED', page: 'wechat' });
          const maxAllowed = errorData.maxAllowed ? errorData.maxAllowed.toLocaleString() : '—';
          if (tg) {
            tg.showAlert(`Превышен лимит. Доступно: ${maxAllowed} ₽`);
          }
          return;
        }

        // Обработка ошибок от платёжной системы (PAYMENT_ERROR с detail строкой)
        if (errorData.error === 'PAYMENT_ERROR' && errorData.detail) {
          track('error_displayed', { error: 'PAYMENT_ERROR', page: 'wechat' });
          const detailMsg = typeof errorData.detail === 'string' ? errorData.detail : errorData.detail.message;
          if (detailMsg) {
            if (tg) {
              tg.showAlert(detailMsg);
            }
            return;
          }
        }

        // Общая обработка: извлекаем понятное сообщение из detail
        const errorMessage = (typeof errorData.detail === 'string' ? errorData.detail : null)
          || errorData.detail?.message
          || (errorData.message !== 'Validation error' ? errorData.message : null)
          || 'Ошибка создания заявки';

        throw new Error(errorMessage);
      }

      const data = await response.json();

      const actualOrderId = data.orderId || data.order_id;

      if (data.success && actualOrderId) {
        logger.log('✅ Заявка WeChat создана:', actualOrderId);
        track('deposit_created', { orderId: actualOrderId, type: 'wechat', amount: cnyAmountToSend.toFixed(2), method: wechatPaymentMethod });
        
        const orderData = {
          orderId: actualOrderId,
          externalId: data.externalId,
          altynPaymentId: data.altynPaymentId,
          type: 'WeChat',
          amount: cnyAmountToSend.toFixed(2), // Сумма в юанях (уже рассчитанная)
          inputAmount: wechatAmount, // Введённая сумма (RUB или USDT)
          inputCurrency: wechatPaymentMethod === 'USDT' ? 'USDT' : 'RUB',
          paymentMethod: wechatPaymentMethod,
          bank: wechatSelectedBank,
          network: wechatUsdtNetwork,
          fullName: wechatFullName,
          paymentDetails: data.paymentDetails,
          status: data.status || 'pending'
        };
        setOrderDetails(orderData);
        
        triggerHaptic('success');
        setCurrentScreen('orderConfirmation');
        
        setWechatPaymentMethod('');
        setWechatAmount('');
        setWechatFullName('');
        setWechatQrCode(null);
        setWechatSelectedBank('');
        setWechatUsdtNetwork('');
        
        loadUserData();
      } else {
        throw new Error(data.message || 'Ошибка создания заявки');
      }
      
    } catch (error) {
      logger.error('❌ Error создания WeChat заявки:', error);
      if (tg) {
        tg.showAlert('Ошибка: ' + error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- DEBUG: Диагностическая панель (убрать после отладки) ---

  // --- Экран ошибки инициализации ---
  if (initError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 24, textAlign: 'center', background: '#030712', fontFamily: 'sans-serif' }}>
        <div>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28, color: '#f87171', fontWeight: 700 }}>!</div>
          <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Ошибка</h2>
          <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>{initError}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 24px', borderRadius: 12, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  // --- Лоадер пока Telegram не инициализирован ---
  if (!isAppReady) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#030712' }}>
        <div style={{ textAlign: 'center' }}>
          <img src="/logo.png?v=2" alt="Kit8 Exchange" style={{ width: 80, height: 'auto', margin: '0 auto 16px', display: 'block', opacity: 0.7 }} />
          <p style={{ color: '#6b7280', fontSize: 14, fontFamily: 'sans-serif' }}>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans" style={{ minHeight: '100vh', background: '#030712', color: '#fff' }}>
      {currentScreen === 'main' && (
        <MainMenu
          setCurrentScreen={navigateTo}
          setProfileActiveSection={setProfileActiveSection}
          exchangeRate={exchangeRate}
          usdtToCnyRate={usdtToCnyRate}
          userFirstName={userFirstName}
          userPhotoUrl={userPhotoUrl}
          verificationStatus={verificationStatus}
        />
      )}
      {currentScreen === 'profile' && (
        <ProfileScreen
          profileTab={profileTab}
          setProfileTab={setProfileTab}
          activeSection={profileActiveSection}
          setActiveSection={setProfileActiveSection}
          requests={requests}
          history={history}
          stats={stats}
          loadUserData={loadUserData}
          tg={tg}
          setOrderDetails={setOrderDetails}
          setCurrentScreen={navigateTo}
          userFirstName={userFirstName}
          userLastName={userLastName}
          telegramUsername={telegramUsername}
          userPhotoUrl={userPhotoUrl}
          paymentLimits={paymentLimits}
          // Altyn KYC props
          verificationStatus={verificationStatus}
          userPhone={userPhone}
          setUserPhone={setUserPhone}
          uploadDocument={uploadDocument}
          registerClient={registerClient}
          sendVerificationCode={sendVerificationCode}
          confirmVerification={confirmVerification}
          checkVerificationStatus={checkVerificationStatus}
          apiUrl={API_URL}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
          userId={userId}
        />
      )}
      {currentScreen === 'deposit' && (
        <DepositScreen
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          amount={amount}
          setAmount={setAmount}
          fullName={fullName}
          setFullName={setFullName}
          qrCode={qrCode}
          setQrCode={setQrCode}
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          usdtNetwork={usdtNetwork}
          setUsdtNetwork={setUsdtNetwork}
          exchangeRate={exchangeRate}
          usdtToCnyRate={usdtToCnyRate}
          handleAlipayDeposit={handleAlipayDeposit}
          limits={limits}
          paymentLimits={paymentLimits}
          // Altyn props
          verificationStatus={verificationStatus}
          sbpBanks={sbpBanks}
          setCurrentScreen={navigateTo}
          setProfileActiveSection={setProfileActiveSection}
          // Динамический расчет курса
          calculateAmount={calculateAmount}
          calculatedAmount={calculatedAmounts.alipay}
          isSubmitting={isSubmitting}
        />
      )}
      {currentScreen === 'wechat' && (
        <WechatScreen
          wechatPaymentMethod={wechatPaymentMethod}
          setWechatPaymentMethod={setWechatPaymentMethod}
          wechatAmount={wechatAmount}
          setWechatAmount={setWechatAmount}
          wechatFullName={wechatFullName}
          setWechatFullName={setWechatFullName}
          wechatQrCode={wechatQrCode}
          setWechatQrCode={setWechatQrCode}
          wechatSelectedBank={wechatSelectedBank}
          setWechatSelectedBank={setWechatSelectedBank}
          wechatUsdtNetwork={wechatUsdtNetwork}
          setWechatUsdtNetwork={setWechatUsdtNetwork}
          exchangeRate={exchangeRate}
          usdtToCnyRate={usdtToCnyRate}
          handleWechatDeposit={handleWechatDeposit}
          limits={limits}
          paymentLimits={paymentLimits}
          // Altyn props
          verificationStatus={verificationStatus}
          sbpBanks={sbpBanks}
          setCurrentScreen={navigateTo}
          setProfileActiveSection={setProfileActiveSection}
          // Динамический расчет курса
          calculateAmount={calculateAmount}
          calculatedAmount={calculatedAmounts.wechat}
          isSubmitting={isSubmitting}
        />
      )}
      {currentScreen === 'support' && <SupportScreen />}
      {currentScreen === 'orderConfirmation' && (
        <OrderConfirmationScreen
          orderDetails={orderDetails}
          usdtToCnyRate={usdtToCnyRate}
          exchangeRate={exchangeRate}
          tg={tg}
          setCurrentScreen={navigateTo}
          setOrderDetails={setOrderDetails}
          apiUrl={API_URL}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ExchangeApp />
    </ErrorBoundary>
  );
}
